import { z } from 'zod';
import { ESTADOS_SEMANA, PRIORIDADES_TAREA } from '../enums.js';
// ---------------------------------------------------------------------------
// Tarea (ciclo-tareas): la gestión semanal de prioridades de Ailen. Reemplaza
// el acceso a Leads (Notion) en el panel. Circuito planificar/ejecutar/cerrar
// (28/09/2026): una Semana numerada, con estado PLANIFICADA -> GUARDADA ->
// CERRADA. "Guardar" congela la lista de tareas (solo quedan los checks);
// "cerrar" saca la foto (TareaRegistroSemanal) de cada tarea activa y abre la
// semana siguiente en PLANIFICADA.
//
// ciclo-tareas-deadline (18/10/2026): cada tarea vence un día de la semana que
// se planifica. El rango válido (lunes a domingo) lo controla el backend, que
// sabe qué semana está activa; acá solo se valida que sea una fecha real.
// ---------------------------------------------------------------------------
/** Fecha sin hora, AAAA-MM-DD, que exista de verdad (rechaza 2026-02-31). */
export const fechaDiaSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida (AAAA-MM-DD)')
    .refine((f) => {
    const d = new Date(`${f}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === f;
}, 'Fecha inválida');
export const tareaCrearSchema = z.object({
    titulo: z.string().trim().min(1, 'El título no puede estar vacío').max(120),
    descripcion: z.string().trim().max(500).nullable().optional(),
    prioridad: z.enum(PRIORIDADES_TAREA).default('MEDIA'),
    venceEl: fechaDiaSchema,
});
export const tareaEditarSchema = z.object({
    titulo: z.string().trim().min(1, 'El título no puede estar vacío').max(120).optional(),
    descripcion: z.string().trim().max(500).nullable().optional(),
    prioridad: z.enum(PRIORIDADES_TAREA).optional(),
    activa: z.boolean().optional(),
    completada: z.boolean().optional(),
    venceEl: fechaDiaSchema.optional(),
});
export const tareaOrdenSchema = z.object({
    ids: z.array(z.string().min(1)).min(1),
});
export const tareaPublicSchema = z.object({
    id: z.string(),
    titulo: z.string(),
    descripcion: z.string().nullable(),
    prioridad: z.enum(PRIORIDADES_TAREA),
    activa: z.boolean(),
    orden: z.number().int(),
    completada: z.boolean(),
    completadaEn: z.string().nullable(),
    // AAAA-MM-DD; null en las tareas creadas antes de que existiera el vencimiento.
    venceEl: z.string().nullable(),
    creadoEn: z.string(),
});
export const semanaPublicSchema = z.object({
    id: z.string(),
    // Número de semana ISO 8601 real (no una cuenta secuencial) + su año ISO.
    numero: z.number().int(),
    anio: z.number().int(),
    estado: z.enum(ESTADOS_SEMANA),
    // Lunes y domingo de la semana ISO (AAAA-MM-DD): el rango válido del vencimiento.
    inicio: z.string(),
    fin: z.string(),
    // El objetivo de la semana (D-044, 19/10/2026); null hasta que se define.
    objetivo: z.string().nullable(),
    creadaEn: z.string(),
    guardadaEn: z.string().nullable(),
    cerradaEn: z.string().nullable(),
});
/** El objetivo de la semana: una frase. Obligatorio para guardar la
 * planificación; se puede cambiar mientras la semana no esté cerrada. */
export const SEMANA_OBJETIVO_MAX = 150;
export const semanaObjetivoSchema = z.object({
    objetivo: z.string().trim().min(1, 'El objetivo no puede estar vacío').max(SEMANA_OBJETIVO_MAX),
});
// Una semana en la tira de puntos de la estadística por tarea (R4).
export const tareaSemanaSchema = z.object({
    numero: z.number().int(),
    anio: z.number().int(),
    cumplida: z.boolean(),
});
export const tareaEstadisticaSchema = z.object({
    tareaId: z.string().nullable(),
    titulo: z.string(),
    prioridad: z.enum(PRIORIDADES_TAREA),
    porcentajeCumplimiento: z.number(),
    totalSemanas: z.number().int(),
    ultimasSemanas: z.array(tareaSemanaSchema),
});
// El % de cumplimiento global de una semana cerrada (todas las tareas activas
// en ese momento), para el gráfico de barras semana a semana.
export const semanaEstadisticaSchema = z.object({
    numero: z.number().int(),
    anio: z.number().int(),
    objetivo: z.string().nullable(),
    totalActivas: z.number().int(),
    cumplidas: z.number().int(),
    porcentajeCumplimiento: z.number(),
});
export const tareaEstadisticasResponseSchema = z.object({
    tareas: z.array(tareaEstadisticaSchema),
    // id de la tarea con menor cumplimiento (mínimo 2 semanas registradas); null
    // si ninguna tarea llega a ese mínimo todavía.
    peorTareaId: z.string().nullable(),
    semanas: z.array(semanaEstadisticaSchema),
    // Cumplidas / total, contando todos los registros de todas las semanas cerradas.
    porcentajeGlobal: z.number(),
    totalTareas: z.number().int(),
    totalSemanasCerradas: z.number().int(),
});
//# sourceMappingURL=tarea.js.map